import { Component, Inject } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { updateCurrencyAmount } from '../core/components/currency-input-calculations';
import { NumpadValue } from '../core/components/numpad.component';
import { ScreenLayout, SCREEN_LAYOUT } from '../core/layout/layout.provider';
import { DataService } from '../data/data.service';
import { Settings } from '../data/models';
import { APP_SETTINGS } from '../data/settings-data.provider';

@Component({
    selector: 'app-adhoc-product',
    styles: [`
    :host {
      display: block;
      height: 100%;
      width: 100%;
    }

    div.is-invalid {
      border-color: var(--bs-danger);
    }
  `],
    template: `
    <div class="modal-header p-1 px-2 p-md-3">
      <h4 class="modal-title" id="exampleModalLabel" i18n>Adhoc Produkt anlegen</h4>
      <button type="button" class="btn-close mx-1" aria-label="Close" i18n-aria-label (click)="modalService.close()"></button>
    </div>
    <div class="modal-body d-flex p-0">
      <div class="d-flex flex-column flex-lg-row w-100">

        <!-- input section for small screens  -->
        <div class="vstack p-2 p-md-3 gap-2 gap-md-3 d-lg-none">
          <div class="row m-0">
            <label for="input-title" class="form-label col-3 col-form-label" i18n>Bezeichnung</label>
            <div class="col-9 ps-4 pe-2">
              <div class="input-group">
                <input type="text" class="form-control fw-bold" id="input-title"
                       [formControl]="name"
                       appSelectOnFocus
                       [class.is-invalid]="name.invalid && name.touched">
                <div class="input-group-text bg-white">
                  <button type="button" class="btn-close" (click)="revertName()"></button>
                </div>
              </div>
            </div>
          </div>

          <div class="row d-flex align-items-center m-0">
            <div class="d-block form-label col-3 mb-0" i18n>Steuersatz</div>
            <div class="col-9 pe-2 ps-4 gap-1 d-flex flex-column" role="group">
              <ng-container *ngFor="let rate of taxRates; first as first">
                <input type="radio" class="btn-check" [id]="'tax-rate-2-'+ rate" name="tax-rate-2"
                    (click)="updateTaxRate(rate)" autocomplete="off" [checked]="first">
                <label class="btn btn-outline-info px-4" for="{{'tax-rate-2-'+ rate}}">{{rate}}%</label>
              </ng-container>
            </div>
          </div>
        </div>

        <div class="d-flex flex-column border-end border-medium">
          <div class="d-flex flex-column align-items-center px-3 pt-0 pt-md-2 pb-2 mb-2 mb-md-3">
            <div class="input-group focus-input-group w-208px w-lg-272px">
              <div class="input-group-text bg-white" [class.is-invalid]="price === 0 && name.touched">
                <button type="button" class="btn-close" (click)="revertAmount()"></button>
              </div>
              <app-currency-input class="form-control fs-5 form-control-lg border-start-0"
                                  [value]="price"
                                  [class.is-invalid]="price === 0 && name.touched"
                                  (valueChange)="updateAmount($event)"></app-currency-input>
            </div>
          </div>
          <app-numpad theme="dark" [showDecimalZeroes]="true" (buttonPress)="handleNumpadEvent($event)"></app-numpad>
        </div>

        <div class="vstack px-3 p-md-3 gap-1 gap-md-3 pb-2">

          <!-- input section for large screens  -->
          <div class="d-none d-lg-flex justify-content-between align-items-center flex-nowrap">
            <div class="w-100">
              <label for="input-title" class="form-label" i18n>Bezeichnung</label>
              <div class="input-group">
                <input type="text" ngbAutofocus class="form-control fw-bold" id="input-title"
                       [formControl]="name"
                       appSelectOnFocus
                       [class.is-invalid]="name.invalid && name.touched">
                <div class="input-group-text bg-white">
                  <button type="button" class="btn-close" (click)="revertName()"></button>
                </div>
              </div>
            </div>
          </div>

          <div class="d-none d-lg-flex justify-content-between align-items-center flex-nowrap">
            <div class="d-flex flex-column justify-content-center">
              <div class="d-block form-label" i18n>Steuersatz</div>
              <div class="d-flex flex-wrap gap-1" role="group">
                <ng-container *ngFor="let rate of taxRates; first as first">
                  <input type="radio" class="btn-check" [id]="'tax-rate-'+ rate" name="tax-rate"
                       (click)="updateTaxRate(rate)" autocomplete="off" [checked]="first">
                  <label class="btn btn-outline-info px-4" for="{{'tax-rate-'+ rate}}">
                    <span class="px-2">{{rate}}%</span>
                  </label>
                </ng-container>
              </div>
            </div>

          </div>

          <div class="border-bottom border-medium d-none d-lg-block"></div>

          <div class="d-flex flex-grow-1 flex-shrink-1"></div>

          <div class="d-none d-lg-flex align-items-end flex-column">
            <span class="fs-5 fw-bold text-muted" i18n>Preis</span>
            <span class="text-info fs-3 fw-bold">{{ price | currency }}</span>
          </div>

          <div class="border-bottom border-medium d-block"></div>

          <button type="button" class="btn btn-lg btn-success text-white w-100 py-2"
                  (click)="saveProduct()"
                  [disabled]="loading"
                  [appLoadingOverlay]="loading"
                  i18n>
            Zum Warenkorb hinzufügen
          </button>

        </div>
      </div>
    </div>
  `,
    standalone: false
})
export class AdHocProductPage {
  loading = false;

  DEFAULT_NAME = $localize`Sonstiges`;
  taxRates: number[];

  price: number = 0;
  name: UntypedFormControl = new UntypedFormControl(this.DEFAULT_NAME, [Validators.required]);
  taxRate: number;

  constructor(@Inject(SCREEN_LAYOUT) public layout: ScreenLayout,
    @Inject(APP_SETTINGS) private readonly settings: Settings,
    public readonly modalService: NgbActiveModal,
    private readonly dataService: DataService
  ) {
    const mainTaxRate = this.settings.taxRates.taxCountry.mainTaxRate;
    const additionalRates = this.settings.taxRates.reduced.map(reduced => reduced.taxRate);
    this.taxRates = [mainTaxRate, ...additionalRates];

    this.taxRate = this.taxRates[0];
  }

  updateAmount(value: number) {
    this.price = value;
  }

  handleNumpadEvent(value: NumpadValue) {
    const updatedAmount = updateCurrencyAmount(this.price, value);
    this.updateAmount(updatedAmount);
  }

  revertAmount() {
    this.price = 0;
  }

  revertName() {
    this.name.patchValue('');
  }

  updateTaxRate(value: number) {
    this.taxRate = value;
  }

  async saveProduct() {
    this.name.markAllAsTouched();

    if (this.price === 0 || this.name.invalid) {
      return;
    }

    this.loading = true;

    try {
      await this.dataService.addAdHocProductToCart({
        title: this.name.value || this.DEFAULT_NAME,
        taxRate: this.taxRate,
        price: this.price
      });
    } finally {
      this.loading = false;
    }

    this.modalService.close();
  }
}
